import { useKeyboard } from '@react-aria/interactions'
import { AddTodo } from 'almanac-ui/icons/16'
import { ReactComponent as CancelIcon } from 'assets/icons/close16.svg'
import { ReactComponent as PostIcon } from 'assets/icons/post16.svg'
import cx from 'classnames'
import { ProseInputHandle } from 'common/ProseInput/Types'
import { Toolbar } from 'common/ProseInput/components/Toolbar'
import { Button } from 'components/Button'
import { CommentForm } from 'components/CommentForm/CommentForm'
import { IconButton } from 'components/IconButton/IconButton'
import { TodoForm } from 'components/TodoForm/TodoForm'
import { TodoFormValues } from 'components/TodoForm/Types'
import { useAuth } from 'hooks/UseAuth'
import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import { ArticleView } from 'types'
import { stripMentionMarkers } from 'utils/comments'
import Styles from './ConvertableCommentTodoForm.module.scss'
import { ConvertableCommentTodoFormProps, ItemType } from './Types'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

/**
 * Form to create a new comment or task. Wrap this component to provide styles
 * or additional interactions.
 */
export const ConvertableCommentTodoForm = observer(
  ({
    hideActionsOnEmpty,
    onActiveChange,
    docStore,
    placeholder,
    activeType = 'comment',
    autoFocus = false,
    onSubmitComment,
    onSubmitTodo,
    styles,
    autoConvertToTodo = false,
    close,
  }: ConvertableCommentTodoFormProps) => {
    if (activeType === 'todo' && !onSubmitTodo) throw new Error('onSubmitTodo is required when activeType is todo')
    const isSystemRedesignEnabled = useIsSystemRedesign()
    const auth = useAuth()
    const [itemType, setItemType] = useState<ItemType>(activeType)

    /* -------------------------------------------------------------------------- */
    /*                                   Comment                                  */
    /* -------------------------------------------------------------------------- */
    const [comment, setComment] = useState('')
    const [mentionedUserIds, setMentionedUserIds] = useState<number[]>([])
    const commentInputRef = useRef<any>(null)

    const handleSubmitComment = async () => {
      clear()
      onSubmitComment(comment, mentionedUserIds)
    }

    const handleChangeComment = (newComment: string, mentionedUserIds: number[]) => {
      setComment(newComment)
      setMentionedUserIds(mentionedUserIds)
      onActiveChange(newComment !== '')

      if (autoConvertToTodo) {
        setComment('')
        convertToTodo(newComment)
      }
    }

    const clearComment = () => {
      setComment('')
      commentInputRef.current?.clear?.()
      setMentionedUserIds([])
      onActiveChange(false)
    }

    /* -------------------------------------------------------------------------- */
    /*                                    Todo                                    */
    /* -------------------------------------------------------------------------- */
    const todoEmptyValues = {
      title: '',
      htmlTitle: '',
      description: '',
      htmlDescription: '',
      dueDate: null,
      assignee: null,
    }
    const todoTitleInputRef = useRef<ProseInputHandle>(null)

    const handleChangeTodoTitle = (_title: string, _htmlTitle: string, mentionedUsersIds: number[]) => {
      setMentionedUserIds(mentionedUsersIds)
    }

    const handleSubmitTodo = async (values: TodoFormValues, mentionedIds: number[]) => {
      clear()
      onSubmitTodo?.(values, mentionedIds)
    }

    const clearTodo = () => {
      setMentionedUserIds([])
    }

    /* -------------------------------------------------------------------------- */
    /*                          Conversion Comment -> Todo                        */
    /* -------------------------------------------------------------------------- */
    const convertToTodo = (newComment?: string) => {
      if (!docStore.isMainLayer) {
        return
      }

      todoTitleInputRef.current?.replaceHTMLString(stripMentionMarkers(comment || newComment || ''))
      onActiveChange(true)
      setItemType('todo')
      todoTitleInputRef.current?.focus()
    }

    const clear = () => {
      clearTodo()
      clearComment()
      setItemType(activeType)
      onActiveChange(false)
      close?.()
    }

    /* -------------------------------------------------------------------------- */
    /*                                  Keyboard                                  */
    /* -------------------------------------------------------------------------- */
    const { keyboardProps } = useKeyboard({
      onKeyDown: (e) => {
        if (e.key === 'Escape') {
          clear()
        }
      },
    })

    const canConvertToTodo =
      onSubmitTodo !== undefined && docStore.isMainLayer && docStore.articleView === ArticleView.Editing && !!auth

    return (
      <>
        <div {...keyboardProps} className={cx(styles.commentFormWrapper, { [Styles.hide]: itemType !== 'comment' })}>
          <CommentForm
            autoFocus={autoFocus}
            isHtmlComment
            placeholder={placeholder}
            inputRef={commentInputRef}
            value={comment}
            onChange={handleChangeComment}
            onSubmit={handleSubmitComment}
            onCancel={clear}
            className={styles.commentForm}
            inputContainerClassName={styles.commentInputContainer}
            inputClassName={styles.commentInput}
            hideActionsOnEmpty={hideActionsOnEmpty}
            footer={
              <>
                <Toolbar.Root>
                  <Toolbar.Bold />
                  <Toolbar.BulletList />
                  <Toolbar.Mention />
                </Toolbar.Root>
                {canConvertToTodo && (
                  <Button variant="tertiary" frontIcon={AddTodo} onPress={() => convertToTodo()}>
                    {isSystemRedesignEnabled ? 'Task' : 'Convert to task'}
                  </Button>
                )}
              </>
            }
            primaryCta={<IconButton variant="primary" title="Post" icon={PostIcon} />}
            secondaryCta={<IconButton variant="tertiary" title="Cancel" icon={CancelIcon} />}
          />
        </div>
        {canConvertToTodo && (
          <div className={cx(styles.todoFormWrapper, { [Styles.hide]: itemType !== 'todo' })}>
            <TodoForm
              titleInputRef={todoTitleInputRef}
              className={styles.todoForm}
              initialValues={todoEmptyValues}
              onSubmit={handleSubmitTodo}
              onCancel={clear}
              onEscape={clear}
              onTitleChange={handleChangeTodoTitle}
              clearOnCancel
              showCheckbox
              submitButtonLabel="Update"
              primaryCta={<IconButton variant="primary" title="Post" icon={PostIcon} />}
              secondaryCta={<IconButton variant="tertiary" title="Cancel" icon={CancelIcon} />}
            />
          </div>
        )}
      </>
    )
  },
)
